/**
 * @namespace GtmTracking
 * @description
 * Implémentation du plan de marquage GTM d'Un Bon Maillot (périmètre BOUTIQUE).
 * Le périmètre CHECKOUT vit dans le Custom Pixel Shopify (cf. docs/gtm/custom-pixel.js).
 *
 * Principe : ce module s'accroche aux sélecteurs déjà présents dans le thème et
 * n'exige (presque) aucune édition des fichiers Liquid ou JS existants — le thème
 * étant régulièrement écrasé par des pulls Shopify.
 *
 * Événements couverts :
 *   view_item · add_to_cart · begin_checkout · view_item_list · select_item
 *   + les événements custom UA-style (megamenu, header, searchacp, collectionPages)
 *
 * `page_view` est poussé côté Liquid (snippets/gtm-head.liquid), avant le conteneur.
 */

import { ThemeEvents } from '@theme/events';

/* ------------------------------------------------------------------ contexte */

const CART_ATTRIBUTE = '_gtm_items';
const MAX_TRACKED_VARIANTS = 40;

/** `fetch` d'origine, capturé avant l'installation du wrapper. */
const nativeFetch = window.fetch.bind(window);

const context = (window.UBM_GTM = window.UBM_GTM || {});
context.catalog = context.catalog || {};
context.items = context.items || {};

const themeGlobal = typeof Theme !== 'undefined' ? Theme : null;

function currencyCode() {
  return themeGlobal?.currency || context.currency || 'EUR';
}

function templateName() {
  return themeGlobal?.template?.name || context.template || '';
}

function cartRoute(suffix) {
  const base = themeGlobal?.routes?.cart_url || '/cart';
  return base + suffix;
}

/* ------------------------------------------------------------ normalisation */

/**
 * Normalise un nom de produit selon le plan de marquage :
 * minuscules, sans emoji, sans caractère spécial (accents CONSERVÉS), espaces en `_`.
 *
 * Implémentation unique et volontairement centralisée : le Custom Pixel ne
 * renormalise rien, il relit la valeur déjà produite ici via l'attribut de panier.
 *
 * @param {string} raw
 * @returns {string}
 */
export function normalizeItemName(raw) {
  if (!raw) return '';
  return String(raw)
    .toLowerCase()
    .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, ' ')
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .trim()
    .replace(/\s+/g, '_');
}

/**
 * Construit `item_variant` au format `numberofjersey_size` (ex. `1maillot_xl`).
 * Les espaces INTERNES à une option sont supprimés, les options jointes par `_`.
 *
 * @param {string} title - Le `variant_title` Shopify, ex. "1 maillot / XL".
 * @returns {string|undefined}
 */
export function normalizeVariantTitle(title) {
  if (!title || title === 'Default Title') return undefined;
  const value = String(title)
    .split('/')
    .map((part) => normalizeItemName(part).replace(/_/g, ''))
    .filter(Boolean)
    .join('_');
  return value || undefined;
}

/** Libellé d'interface : minuscules, espaces normalisés (les espaces sont conservés). */
function label(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/* ------------------------------------------------------------------- dataLayer */

function pushEcommerce(event, ecommerce, extra) {
  window.dataLayer = window.dataLayer || [];
  // Systématique, y compris là où le plan de marquage ne le précise pas.
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce, ...extra });
}

function pushCustomEvent(eventCategory, eventAction, eventLabel) {
  window.dataLayer = window.dataLayer || [];
  const payload = { event: 'event', eventCategory, eventAction };
  if (eventLabel !== undefined && eventLabel !== '') payload.eventLabel = eventLabel;
  window.dataLayer.push(payload);
}

/* --------------------------------------------------------- registre catalogue */

/**
 * Alimente `UBM_GTM.catalog` depuis les `data-gtm-*` des cartes produit.
 * Permet de retrouver les catégories d'un produit ajouté au panier depuis
 * un quick-add ou un cross-sell, où seule la variante est connue.
 */
function indexProductCards(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('product-card[data-gtm-id]').forEach((card) => {
    const id = card.dataset.gtmId;
    if (!id) return;
    context.catalog[id] = {
      ...context.catalog[id],
      title: card.dataset.gtmName,
      c: card.dataset.gtmCat,
      c2: card.dataset.gtmCat2,
      c4: card.dataset.gtmCat4,
    };
  });
}

/** Catégories d'un produit : map persistée (variante) puis catalogue (produit). */
function resolveCategories(productId, variantId) {
  const persisted = context.items[String(variantId)];
  const cataloged = context.catalog[String(productId)] || {};
  return {
    c: persisted?.c || cataloged.c || 'undefined',
    c2: persisted?.c2 || cataloged.c2 || 'undefined',
    c4: persisted?.c4 || cataloged.c4 || 'no',
  };
}

/** Construit un item GA4 complet depuis une ligne de panier Shopify. */
function lineItemToGa4(line) {
  const categories = resolveCategories(line.product_id, line.variant_id || line.id);
  const unitPrice = (line.final_price ?? line.price ?? 0) / 100;
  return {
    item_id: String(line.product_id),
    item_name: normalizeItemName(line.product_title || line.title),
    item_category: categories.c,
    item_category2: categories.c2,
    item_category3: line.selling_plan_allocation ? 'subscription' : 'unic',
    item_category4: categories.c4,
    item_variant: normalizeVariantTitle(line.variant_title),
    price: unitPrice,
    quantity: line.quantity || 1,
  };
}

function sumValue(items) {
  return Number(items.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2));
}

/* ------------------------------------------------- pont boutique -> checkout */

/**
 * Fusionne les items ajoutés dans l'attribut de panier `_gtm_items`.
 * Cet attribut persiste jusque dans la commande : c'est lui qui porte les
 * catégories jusqu'au Custom Pixel (checkout) et jusqu'au purchase server-side
 * du tunnel Just, qui n'ont accès ni aux collections ni aux metafields.
 */
function persistTrackingAttribute(lines, gaItems) {
  lines.forEach((line, index) => {
    const variantId = String(line.variant_id || line.id);
    const item = gaItems[index];
    if (!variantId || !item) return;
    context.items[variantId] = {
      n: item.item_name,
      c: item.item_category,
      c2: item.item_category2,
      c3: item.item_category3,
      c4: item.item_category4,
      v: item.item_variant,
    };
  });

  // Plafonnement : les attributs de panier ont une taille limitée.
  const keys = Object.keys(context.items);
  if (keys.length > MAX_TRACKED_VARIANTS) {
    keys.slice(0, keys.length - MAX_TRACKED_VARIANTS).forEach((key) => delete context.items[key]);
  }

  return nativeFetch(cartRoute('/update.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { [CART_ATTRIBUTE]: JSON.stringify(context.items) } }),
  }).catch(() => {
    /* le tracking ne doit jamais casser le parcours d'achat */
  });
}

/* ------------------------------------------------------------- Lot 3 : panier */

/**
 * Observe les ajouts au panier sans modifier product-form.js / quick-add.js / cross-sell.js.
 *
 * /!\ `Response.json()` ne consomme le flux qu'une seule fois, et le thème lit
 * déjà la réponse (product-form.js). On lit donc une COPIE et on retourne
 * l'original intact, sinon tous les ajouts échouent en `body stream already read`.
 */
function installCartAddObserver() {
  const wrapped = function (...args) {
    const [input, init] = args;
    const url = String((typeof input === 'string' ? input : input?.url) || '');
    const method = String(init?.method || input?.method || 'GET').toUpperCase();
    // Transmission intégrale : ce wrapper est global et doit rester transparent
    // pour tous les autres scripts de la page (Just, CMP, apps Shopify).
    const promise = nativeFetch(...args);

    if (method !== 'POST' || !/\/cart\/add(\.js)?(\?|$)/.test(url)) return promise;

    return promise.then((response) => {
      if (response?.ok) {
        response
          .clone()
          .json()
          .then(handleCartAdd)
          .catch(() => {});
      }
      return response; // original jamais consommé
    });
  };

  window.fetch = wrapped;
}

function handleCartAdd(payload) {
  const lines = Array.isArray(payload?.items) ? payload.items : payload?.id ? [payload] : [];
  if (!lines.length) return;

  const items = lines.map(lineItemToGa4);

  pushEcommerce('add_to_cart', {
    currency: currencyCode(),
    value: sumValue(items),
    items,
  });

  persistTrackingAttribute(lines, items);
  scheduleCartRefresh();
}

/* ------------------------------------------------------- cache panier (sync) */

// Amorcé depuis Liquid (snippets/gtm-head.liquid) : sans cela, un clic sur
// « Commander » dans les premières centaines de ms suivant le chargement — ou
// juste après un ajout au panier — ne pousserait AUCUN begin_checkout.
let cartCache = context.cart || null;
let cartRefreshTimer = null;

/**
 * `begin_checkout` doit être poussé de façon SYNCHRONE avant la navigation :
 * on ne peut donc pas aller chercher /cart.js au moment du clic. Le panier est
 * tenu à jour en arrière-plan.
 */
function refreshCart() {
  return nativeFetch(cartRoute('.js'), { headers: { Accept: 'application/json' } })
    .then((response) => response.json())
    .then((cart) => {
      cartCache = cart;
      if (cart?.attributes?.[CART_ATTRIBUTE]) {
        try {
          // FUSION, pas remplacement : l'écriture de l'attribut est asynchrone et peut
          // ne pas encore avoir atterri. La map en mémoire est la plus récente.
          const stored = JSON.parse(cart.attributes[CART_ATTRIBUTE]) || {};
          context.items = { ...stored, ...context.items };
        } catch (error) {
          /* attribut corrompu : on garde la map en mémoire */
        }
      }
      return cart;
    })
    .catch(() => null);
}

function scheduleCartRefresh() {
  clearTimeout(cartRefreshTimer);
  cartRefreshTimer = setTimeout(refreshCart, 300);
}

function pushBeginCheckout(checkoutMethod) {
  const lines = cartCache?.items || [];
  if (!lines.length) return;

  const items = lines.map(lineItemToGa4);
  pushEcommerce(
    'begin_checkout',
    { currency: currencyCode(), value: sumValue(items), items },
    checkoutMethod ? { checkout_method: checkoutMethod } : undefined
  );
}

/* --------------------------------------------------- Lot 3 : fiche produit */

function currentProductItem(variant) {
  const product = context.product;
  if (!product) return null;

  const price = variant?.price != null ? variant.price / 100 : product.price;

  return {
    item_id: String(product.id),
    item_name: normalizeItemName(product.title),
    item_category: product.c,
    item_category2: product.c2,
    price: Number(price),
  };
}

function pushViewItem(variant) {
  const item = currentProductItem(variant);
  if (!item) return;
  pushEcommerce('view_item', {
    currency: currencyCode(),
    value: item.price,
    items: [item],
  });
}

/* ------------------------------------------- Lot 4 : listes de produits */

const listState = new Map(); // listName -> { sent:Set<string>, pending:Map<string,object> }
let listFlushTimer = null;

/** Résout (et mémorise) le nom de liste d'un conteneur. */
function resolveListName(container) {
  if (!container) return templateName() || 'unknown';
  if (container.dataset.gtmListResolved) return container.dataset.gtmListResolved;

  let name = (container.dataset.gtmList || '').trim();

  if (!name) {
    if (templateName() === 'index') {
      // Conforme au « carrouselhp1|carrouselhp2 » du plan. Numérotation par POSITION
      // DANS LE DOM et non par ordre d'apparition à l'écran : sans cela, un même
      // carrousel changerait de numéro selon l'ordre de scroll ou de clic sur les onglets.
      const autoNamed = Array.from(document.querySelectorAll('[data-gtm-list]')).filter(
        (element) => !(element.dataset.gtmList || '').trim()
      );
      name = `carrouselhp${autoNamed.indexOf(container) + 1}`;
    } else {
      name = window.location.pathname;
    }
  }

  container.dataset.gtmListResolved = name;
  return name;
}

function cardToListItem(card, index) {
  return {
    item_id: String(card.dataset.gtmId),
    item_name: normalizeItemName(card.dataset.gtmName),
    index,
    price: Number(card.dataset.gtmPrice || 0),
    currency: currencyCode(),
  };
}

/** Index 1-based de la carte parmi celles de sa liste. */
function cardIndex(card, container) {
  if (!container) return 1;
  const cards = Array.from(container.querySelectorAll('product-card[data-gtm-id]'));
  return cards.indexOf(card) + 1 || 1;
}

function flushLists() {
  listState.forEach((state, listName) => {
    if (!state.pending.size) return;
    const items = Array.from(state.pending.values()).sort((a, b) => a.index - b.index);
    state.pending.clear();
    pushEcommerce('view_item_list', { item_list_name: listName, items });
  });
}

const listObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const card = entry.target;
      listObserver.unobserve(card);

      const container = card.closest('[data-gtm-list]');
      const listName = resolveListName(container);
      const state = listState.get(listName) || { sent: new Set(), pending: new Map() };
      listState.set(listName, state);

      const id = String(card.dataset.gtmId);
      if (state.sent.has(id)) return;
      state.sent.add(id);
      state.pending.set(id, cardToListItem(card, cardIndex(card, container)));
    });

    clearTimeout(listFlushTimer);
    listFlushTimer = setTimeout(flushLists, 250);
  },
  { threshold: 0.5 }
);

function observeProductCards(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('product-card[data-gtm-id]').forEach((card) => {
    if (card.dataset.gtmObserved) return;
    card.dataset.gtmObserved = '1';
    listObserver.observe(card);
  });
}

let lastSelectedItem = null;

function pushSelectItem(card) {
  // Un double-clic impatient pendant une navigation lente pousserait deux fois.
  const now = Date.now();
  const signature = `${card.dataset.gtmId}|${now - (now % 1000)}`;
  if (signature === lastSelectedItem) return;
  lastSelectedItem = signature;

  const container = card.closest('[data-gtm-list]');
  const listName = resolveListName(container);
  pushEcommerce('select_item', {
    item_list_name: listName,
    items: [cardToListItem(card, cardIndex(card, container))],
  });
}

/* --------------------------------------------------- Lot 5 : événements custom */

function headerElementName(target) {
  if (target.closest('.header__logo')) return 'logo';
  if (target.closest('cart-icon, .header__icon-cart')) return 'cart';
  if (target.closest('.header__icon-account')) return 'login';
  // Volontairement limité au bouton : `.search-popup` engloberait les résultats
  // d'autocomplétion, qui relèvent de `searchacp`.
  if (target.closest('.header__icon-search')) return 'search';
  if (target.closest('.dropdown-localization')) {
    const isCurrency =
      target.closest('.currency-code') ||
      target.closest('.localization-form__country-selector') ||
      target.closest('[data-ref="country-input-item"]');
    return isCurrency ? 'currency' : 'language';
  }
  return null;
}

function searchResultCategory(item) {
  if (item.classList.contains('predictive-search__item-product')) return 'produits';
  if (item.classList.contains('predictive-search__item-collection')) return 'collection';
  return 'pages et articles';
}

function searchResultLabel(item) {
  const title = item.querySelector('p, span');
  return label(title ? title.textContent : item.textContent);
}

function handleMegamenuClick(target) {
  // `.menu-block__link` couvre le mégamenu AVANCÉ (blocks/menu.liquid), qui est
  // l'implémentation réellement configurée dans sections/header-group.json.
  const link = target.closest('.header__submenu-link, .header__menu-link, .menu-block__link');
  if (!link) return false;

  const root = link.closest('dropdown-component, .header__menu-item');
  const parentLabel = label(root?.querySelector('.header__menu-link-text')?.textContent);
  const linkLabel = label(link.textContent);

  // Le bouton parent ouvre le menu : c'est bien un clic sur une catégorie sport,
  // mais il n'y a pas encore de sous-catégorie.
  const isParentToggle = link.classList.contains('header__menu-link--parent');

  if (isParentToggle) {
    pushCustomEvent('megamenu', linkLabel || parentLabel);
  } else {
    pushCustomEvent('megamenu', parentLabel || linkLabel, linkLabel);
  }
  return true;
}

function handleDocumentClick(event) {
  const target = event.target;
  if (!target || typeof target.closest !== 'function') return;

  /* --- select_item (Lot 4) --------------------------------------------
     Délégation en phase de CAPTURE : s'exécute avant le handler de
     `product-card.js`, donc avant `window.location.href = …` comme avant
     la navigation native des <a>/<button> descendants. Aucune édition du
     fichier du thème n'est nécessaire. */
  const card = target.closest('product-card[data-gtm-id]');
  const isNonSelectionControl = target.closest(
    'quick-add-component, add-to-wishlist-button, .splide__arrow, .splide__pagination'
  );
  if (card && !isNonSelectionControl) {
    // Les pastilles de couleur naviguent vers la variante (product-card-swatches.js:15) :
    // ce sont bien des sélections produit, on ne les exclut pas.
    pushSelectItem(card);
  }

  /* --- begin_checkout -------------------------------------------------- */
  if (target.closest('[data-ref="checkout-button"]')) {
    pushBeginCheckout('shopify');
  }
  if (target.closest('just-pay-button')) {
    pushBeginCheckout('just');
  }

  /* --- header ---------------------------------------------------------- */
  const element = headerElementName(target);
  if (element) pushCustomEvent('header', element);

  /* --- megamenu -------------------------------------------------------- */
  if (target.closest('.header__menu-desktop, .header__menu-item, .header__submenu--megamenu, .menu-block')) {
    handleMegamenuClick(target);
  }

  /* --- searchacp ------------------------------------------------------- */
  const searchItem = target.closest(
    '.predictive-search__item-product, .predictive-search__item-collection, .predictive-search__item-page, .predictive-search__item-article'
  );
  if (searchItem) {
    pushCustomEvent('searchacp', searchResultCategory(searchItem), searchResultLabel(searchItem));
  }

  /* --- collectionPages -------------------------------------------------- */
  if (templateName().startsWith('collection')) {
    const chipset = target.closest('.navigation-item');
    if (chipset) {
      pushCustomEvent('collectionPages', 'chipsets', label(chipset.textContent));
    }

    const filterToggle = target.closest(
      '[data-ref="sidebar-toggle"], [data-ref="filter-dropdown"] [data-ref="dropdown-toggle"]'
    );
    if (filterToggle) {
      pushCustomEvent('collectionPages', 'filterOpen');
    }
  }
}

/* ------------------------------------------------------------------- init */

function init() {
  installCartAddObserver();

  indexProductCards();
  observeProductCards();

  if (context.product) pushViewItem();

  document.addEventListener('click', handleDocumentClick, true);

  // Le prix change avec la variante sélectionnée.
  document.addEventListener(ThemeEvents.variantUpdated, (event) => {
    pushViewItem(event.detail?.resource);
  });

  // La grille collection est intégralement remplacée au filtre / tri / pagination.
  document.addEventListener(ThemeEvents.filtersUpdated, () => {
    // Choix explicite : filtrer/trier/paginer produit un NOUVEL ensemble de résultats,
    // donc une nouvelle impression de liste. Sans cette purge, le dédoublonnage par
    // `sent` empêcherait tout re-push pour les produits déjà vus — le nom de liste
    // (`{{ collection.url }}`) ne changeant pas d'un filtre à l'autre.
    listState.clear();
    indexProductCards();
    observeProductCards();
  });

  document.addEventListener(ThemeEvents.cartUpdated, scheduleCartRefresh);

  // Cartes injectées en asynchrone (recommandations produit, cross-sell, tiroir panier).
  const domObserver = new MutationObserver((mutations) => {
    const hasNewCards = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === 1 &&
          (node.matches?.('product-card[data-gtm-id]') || node.querySelector?.('product-card[data-gtm-id]'))
      )
    );
    if (!hasNewCards) return;
    indexProductCards();
    observeProductCards();
  });
  domObserver.observe(document.body, { childList: true, subtree: true });

  refreshCart();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
